import { useRouteError, isRouteErrorResponse, useNavigate, Link, useParams } from 'react-router-dom';
import { useState, useEffect } from 'react';

// material-ui
import { Theme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import CardMedia from '@mui/material/CardMedia';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Collapse from '@mui/material/Collapse';
import Tooltip from '@mui/material/Tooltip';
import IconButton from '@mui/material/IconButton';

// icons
import ReloadOutlined from '@ant-design/icons/ReloadOutlined';
import HomeOutlined from '@ant-design/icons/HomeOutlined';
import ArrowLeftOutlined from '@ant-design/icons/ArrowLeftOutlined';
import CopyOutlined from '@ant-design/icons/CopyOutlined';
import CheckOutlined from '@ant-design/icons/CheckOutlined';

// project imports
import { APP_DEFAULT_PATH } from 'config';
import AuthWrapper from 'sections/auth/AuthWrapper';
import useTenant from 'hooks/useTenant';
import MainCard from 'components/MainCard';
import useAuth from 'hooks/useAuth';

// assets
import error500 from 'assets/images/maintenance/Error500.png';

// ==============================|| ROUTE ERROR CONTENT COMPONENT ||============================== //

interface RouteErrorContentProps {
  error?: any;
  onReset?: () => void;
  isInsideLayout?: boolean;
}

export function RouteErrorContent({ error, onReset, isInsideLayout = true }: RouteErrorContentProps) {
  const navigate = useNavigate();
  const { uniqueCode } = useParams<{ uniqueCode: string }>();
  const downSM = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));
  const { isLoggedIn } = useAuth();

  const [showError, setShowError] = useState(false);
  const [copied, setCopied] = useState(false);

  // Extract human-readable error message and stack trace
  let errorMessage = 'An unexpected application error occurred.';
  let errorDetails = '';

  if (isRouteErrorResponse(error)) {
    errorMessage = `${error.status} ${error.statusText}`;
    errorDetails = error.data ? JSON.stringify(error.data, null, 2) : error.statusText;
  } else if (error instanceof Error) {
    errorMessage = error.message;
    errorDetails = error.stack || error.message;
  } else if (typeof error === 'string') {
    errorMessage = error;
    errorDetails = error;
  } else if (error && typeof error === 'object') {
    errorMessage = error.message || error.statusText || 'Unexpected Application Error';
    errorDetails = JSON.stringify(error, Object.getOwnPropertyNames(error), 2);
  }

  const handleToggleError = () => setShowError((prev) => !prev);

  const handleCopy = () => {
    const fullText = `Error: ${errorMessage}\n\nDetails:\n${errorDetails}`;
    navigator.clipboard.writeText(fullText);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleReload = () => {
    if (onReset) onReset();
    window.location.reload();
  };

  const handleGoBack = () => {
    if (onReset) onReset();
    navigate(-1);
  };

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '85vh',
        width: '100%',
        py: { xs: 3, sm: 4 },
        backgroundColor: "#FFFFFF"
      }}
    >
      <Stack
        alignItems="center"
        textAlign="center"
        spacing={2}
        sx={{ maxWidth: 600, width: '100%', px: 2 }}
      >
        <Box sx={{ width: { xs: 220, sm: 270 }, my: 1 }}>
          <CardMedia component="img" src={error500} alt="Unexpected Application Error" />
        </Box>

        <Stack spacing={0.75} mt={1}>
          <Typography variant={downSM ? 'h4' : 'h3'} sx={{ fontWeight: 600 }}>
            Something went wrong
          </Typography>

          <Typography color="text.secondary" variant="body2" sx={{ maxWidth: 450, mx: 'auto' }}>
            An unexpected error occurred while rendering this page. Please try again.
          </Typography>
        </Stack>

        {/* Action Buttons */}
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={1.5}
          sx={{ mt: 2, alignItems: 'center', justifyContent: 'center' }}
        >
          <Button
            variant="contained"
            color="primary"
            startIcon={<ReloadOutlined />}
            onClick={handleReload}
            sx={{ textTransform: 'none', px: 2.5, borderRadius: 1.5 }}
          >
            Reload Page
          </Button>

          <Button
            variant="outlined"
            color="secondary"
            startIcon={<ArrowLeftOutlined />}
            onClick={handleGoBack}
            sx={{ textTransform: 'none', px: 2, borderRadius: 1.5 }}
          >
            Go Back
          </Button>

          {isLoggedIn ? (
            <Button
              component={Link}
              to={APP_DEFAULT_PATH}
              variant="outlined"
              color="primary"
              startIcon={<HomeOutlined />}
              onClick={() => onReset?.()}
              sx={{ textTransform: 'none', px: 2, borderRadius: 1.5 }}
            >
              Back To Home
            </Button>
          ) : (
            <Button
              component={Link}
              to={uniqueCode ? `/${uniqueCode}/login` : '/login'}
              variant="outlined"
              color="primary"
              startIcon={<HomeOutlined />}
              onClick={() => onReset?.()}
              sx={{ textTransform: 'none', px: 2, borderRadius: 1.5 }}
            >
              Back To Login
            </Button>
          )}
        </Stack>

        {/* Error Details Section */}
        <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%' }}>
          <Button
            size="small"
            onClick={handleToggleError}
            sx={{ textTransform: 'none', color: 'text.secondary', fontSize: '0.8125rem' }}
          >
            {showError ? 'Hide Error Details ▲' : 'Show Error Details ▼'}
          </Button>

          <Collapse in={showError} sx={{ width: '100%' }}>
            <Box
              sx={{
                p: 1.5,
                mt: 1,
                bgcolor: (theme) => (theme.palette.mode === 'dark' ? 'grey.800' : 'grey.100'),
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'divider',
                textAlign: 'left',
                maxHeight: '220px',
                overflow: 'auto',
                width: '100%',
                position: 'relative'
              }}
            >
              <Box sx={{ position: 'sticky', top: 0, float: 'right', zIndex: 1 }}>
                <Tooltip title={copied ? 'Copied!' : 'Copy Error'}>
                  <IconButton size="small" onClick={handleCopy} color={copied ? 'success' : 'default'}>
                    {copied ? <CheckOutlined /> : <CopyOutlined />}
                  </IconButton>
                </Tooltip>
              </Box>

              <Typography
                variant="subtitle2"
                sx={{ color: 'error.main', fontWeight: 600, mb: 0.5, pr: 4, wordBreak: 'break-word' }}
              >
                {errorMessage}
              </Typography>

              {errorDetails && (
                <Typography
                  variant="caption"
                  component="pre"
                  sx={{
                    fontFamily: 'monospace',
                    fontSize: '0.75rem',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-all',
                    color: 'text.secondary',
                    m: 0
                  }}
                >
                  {errorDetails}
                </Typography>
              )}
            </Box>
          </Collapse>
        </Box>
      </Stack>
    </Box>
  );
}

// ==============================|| ROUTE ERROR BOUNDARY (FOR STANDALONE/AUTH) ||============================== //

export default function RouteErrorBoundary() {
  const error: any = useRouteError();
  const { uniqueCode } = useParams<{ uniqueCode: string }>();
  const { branding, isLoading: isBrandingLoading, fetchBranding } = useTenant();
  const { isLoggedIn } = useAuth();

  useEffect(() => {
    if (uniqueCode && !branding) {
      fetchBranding(uniqueCode);
    }
  }, [uniqueCode, branding, fetchBranding]);

  if (isLoggedIn) {
    return (
      <MainCard content={false}>
        <RouteErrorContent error={error} isInsideLayout={false} />
      </MainCard>
    );
  }

  return (
    <AuthWrapper
      logoUrl={branding?.tenantLogoUrl}
      companyName={branding?.companyName}
      loading={isBrandingLoading && !branding}
    >
      <RouteErrorContent error={error} isInsideLayout={false} />
    </AuthWrapper>
  );
}
